import { ArrowForward, FilterList, Settings } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputBase,
  Menu,
  MenuItem,
  Paper,
  Tooltip,
} from "@mui/material";
import { useState } from "react";
import { useAtom } from "jotai";
import { useLocalStorage } from "usehooks-ts";
import { useNavigate, useSearchParams } from "react-router-dom";
import { grey } from "../../../colors";
import { Feature } from "../../../featureFlags/featureFlagConfig";
import useFeatureFlag from "../../../featureFlags/useFeatureFlag";
import { useQuestionInputStore } from "../../../store/home/useQuestionInputStore";
import useUserContext from "../../Authorization/useUserContext";
import EUComplianceText from "../../common/EUComplianceText";
import { QuestionTargetSalesData, SidebarActivePanelAtom } from "../state/DeepchatState";
import { ThinkingStepsAtom } from "../state/DeepchatState";
import { SIDEBAR_STATE } from "../../../constants/sidebarEnums";

// Simple validation method to block the majority of basic nonsense being submitted to the query input
export const isValidQuestion = (question: string) => {
  const text = String(question).trim();
  const words = text.split(" ").filter((word) => word.length > 0);
  // Remove all spaces for length check
  const nonSpaceLength = text.replace(/\s/g, "").length;

  // Check if first three words are all different (case-insensitive)
  if (words.length > 2) {
    const [w1, w2, w3] = words.slice(0, 3).map((w) => w.toLowerCase());
    if (w1 === w2 || w1 === w3 || w2 === w3) {
      return false;
    }
  }

  return nonSpaceLength > 8 && words.length > 2 && words.some((word) => word.length > 2);
};

export interface QuestionInputProps {
  placeholder?: string;
  hideSalesData?: boolean;
}

export default function QuestionInput(
  { placeholder, hideSalesData }: QuestionInputProps = {
    hideSalesData: false,
  },
) {
  const structuredData_feature = useFeatureFlag(Feature.StructuredData);
  const user = useUserContext();
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();

  const [checkboxSalesData, setCheckboxSalesData] = useAtom(QuestionTargetSalesData);

  const updateCheckboxSalesData = (checked: boolean) => {
    setCheckboxSalesData(checked);

    // Try and keep the URI in sync with the checkbox state
    setSearchParams((_) => ({ ..._, salesdata: checked ? 1 : 0 }));
  };
  const [clarificationEnabled, setClarificationEnabled] = useLocalStorage("deepchat:clarificationEnabled", true);

  // Settings menu state
  const [settingsAnchor, setSettingsAnchor] = useState<null | HTMLElement>(null);
  const settingsOpen = Boolean(settingsAnchor);

  // Hide the the following controls when "Sales Data" is selected
  const hideFilters = checkboxSalesData;

  const {
    disableFilters,
    loading,
    question,
    LeftCallout,
    RightCallout,
    submitQuestion,
    orientation,
    setQuestion,
    setHideFilterDrawer,
    hideFilterDrawer,
  } = useQuestionInputStore();

  const [, setActivePanel] = useAtom(SidebarActivePanelAtom);
  const [thinkingSteps] = useAtom(ThinkingStepsAtom);
  const showSpinner = loading && thinkingSteps.length === 0;

  const handleSubmitQuestion = (question: string) => {
    if (!checkboxSalesData) {
      submitQuestion(question, clarificationEnabled);
    } else {
      navigate(`/salesdata/?question=${question}`);
    }
  };

  return (
    <Box>
      <Paper
        component="form"
        elevation={1}
        sx={{
          p: "10px 16px",
          display: "flex",
          width: "100%",
          flexDirection: disableFilters ? "row" : "column",
          borderRadius: "32px",
          boxShadow: "0 4px 16px rgba(0, 0, 0, 0.05), 0 1px 4px rgba(0, 0, 0, 0.05)",
          transition: "border-color 0.2s ease, box-shadow 0.2s ease",
        }}
      >
        <InputBase
          sx={{ ml: 1, flex: 1, p: disableFilters ? "" : "0.5rem", mt: 1 }}
          disabled={loading}
          multiline
          placeholder={placeholder || "Select filters to narrow down the data, then ask a question..."}
          value={question}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              // Block page from reloading on "Enter"
              event.preventDefault();
              handleSubmitQuestion(question);
            }
          }}
          onChange={(event) => {
            setQuestion(event.target.value);
          }}
        />
        <Box display="flex" justifyContent="space-between" alignItems="center" margin="0rem 1rem">
          {!disableFilters && !hideFilters && (
            <LeftCallout placement={{ top: "100%", left: "100%" }} x="left" y="top" axis="y">
              <Button
                onClick={() => {
                  // Expand the filters panel in the sidebar
                  setActivePanel(SIDEBAR_STATE.FILTERS);
                }}
                size="small"
                startIcon={<FilterList />}
                sx={{ borderRadius: "32px" }}
              >
                Filter
              </Button>
            </LeftCallout>
          )}

          <Tooltip title="Chat settings">
            <IconButton size="small" onClick={(e) => setSettingsAnchor(e.currentTarget)} sx={{ color: grey[600] }}>
              <Settings fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={settingsAnchor}
            open={settingsOpen}
            onClose={() => setSettingsAnchor(null)}
            anchorOrigin={{ vertical: "top", horizontal: "left" }}
            transformOrigin={{ vertical: "bottom", horizontal: "left" }}
            slotProps={{ paper: { sx: { borderRadius: "12px", minWidth: 220 } } }}
          >
            <MenuItem dense onClick={() => setClarificationEnabled((prev) => !prev)} sx={{ py: 0.5 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(clarificationEnabled)}
                    size="small"
                    onChange={(e) => {
                      e.stopPropagation();
                      setClarificationEnabled(e.target.checked);
                    }}
                  />
                }
                label="Clarifying Questions"
                sx={{ "& .MuiTypography-root": { fontSize: "0.875rem" } }}
              />
            </MenuItem>
            {structuredData_feature.enabled && user.selectedRoles.includes("commercial") && !hideSalesData && (
              <MenuItem
                dense
                onClick={() => {
                  updateCheckboxSalesData(!checkboxSalesData);
                  setSettingsAnchor(null);
                }}
                sx={{ py: 0.5 }}
              >
                <FormControlLabel
                  control={<Checkbox checked={checkboxSalesData} size="small" />}
                  label="Sales Data"
                  sx={{ pointerEvents: "none", "& .MuiTypography-root": { fontSize: "0.875rem" } }}
                />
              </MenuItem>
            )}
          </Menu>
          <Box sx={{ flex: 1 }}></Box>
          {showSpinner ? ( //Spinner will load in case a question doesn't have thinking steps involved.
            <CircularProgress sx={{ p: "0.25em", m: "4px" }} />
          ) : (
            <Box display="flex">
              <RightCallout
                x={orientation.x}
                y={orientation.y}
                axis={orientation.axis}
                placement={orientation.placement}
              >
                <Button
                  disableElevation
                  disabled={!question}
                  variant="contained"
                  aria-label="search"
                  sx={{ padding: "8px", margin: "4px", minWidth: "unset", color: grey[700], borderRadius: "50%" }}
                  onClick={() => handleSubmitQuestion(question)}
                >
                  <ArrowForward />
                </Button>
              </RightCallout>
            </Box>
          )}
        </Box>
      </Paper>
      <EUComplianceText />
    </Box>
  );
}
